import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { formatUsd } from '@/lib/rebatesData';

const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Confirm a batch move before anything is written: how many, how much, by
 * store, and (for sent / received) the day it actually happened — he may be
 * marking Monday's mail on Wednesday.
 *
 * mode: 'send' (logged → submitted), 'receive' (submitted → received),
 *       'undo' (a sent batch back to logged).
 */
export default function RebateBatchDialog({ open, onOpenChange, mode, summary, busy, onConfirm }) {
  const { t } = useTranslation();
  const [day, setDay] = useState(todayLocal());

  useEffect(() => {
    if (open) setDay(todayLocal());
  }, [open]);

  if (!summary) return null;
  const withDate = mode === 'send' || mode === 'receive';

  return (
    <AlertDialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <AlertDialogContent className="bg-[hsl(var(--card))] border-border" data-testid="rebate-batch-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-foreground">
            {t(`rebateTracker.batch.${mode}Title`, { count: summary.count })}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-muted-foreground">
            {t(`rebateTracker.batch.${mode}Help`)}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="rounded-md border border-border divide-y divide-border text-sm" data-testid="rebate-batch-summary">
          {summary.byStore.map((s) => (
            <div key={s.store} className="flex items-center justify-between px-3 py-2">
              <span className="text-foreground">
                {s.store} <span className="text-muted-foreground">· {t('rebateTracker.batch.receipts', { count: s.count })}</span>
              </span>
              <span className="tabular-nums text-foreground">{formatUsd(s.total)}</span>
            </div>
          ))}
          <div className="flex items-center justify-between px-3 py-2 font-semibold">
            <span className="text-foreground">{t('rebateTracker.batch.total')}</span>
            <span className="tabular-nums text-[hsl(var(--success))]" data-testid="rebate-batch-total">
              {formatUsd(summary.total)}
            </span>
          </div>
        </div>

        {summary.unmovable ? (
          <p className="text-xs text-[hsl(var(--warning))]">
            {t('rebateTracker.batch.unmovable', { count: summary.unmovable })}
          </p>
        ) : null}

        {withDate ? (
          <div className="space-y-1.5">
            <Label htmlFor="rebate-batch-day" className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
              {t(mode === 'send' ? 'rebateTracker.batch.sentOn' : 'rebateTracker.batch.receivedOn')}
            </Label>
            <Input
              id="rebate-batch-day"
              type="date"
              value={day}
              max={todayLocal()}
              onChange={(e) => setDay(e.target.value)}
              className="h-9 w-[180px] bg-[hsl(var(--muted))] border-border"
              data-testid="rebate-batch-day"
            />
          </div>
        ) : null}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} className="border-border text-foreground hover:bg-[hsl(var(--muted))]">
            {t('actions.cancel')}
          </AlertDialogCancel>
          <Button
            onClick={() => onConfirm(withDate ? day : null)}
            disabled={busy || !summary.count || (withDate && !day)}
            className={
              mode === 'undo'
                ? 'bg-[hsl(var(--destructive))] text-destructive-foreground hover:bg-[hsl(var(--destructive))]/90'
                : 'bg-primary text-primary-foreground hover:bg-primary/90'
            }
            data-testid="rebate-batch-confirm"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {t(`rebateTracker.batch.${mode}Confirm`, { count: summary.count, total: formatUsd(summary.total) })}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
